import { useEffect, useMemo, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, ArrowRight } from "lucide-react";
import { MaskLine, Reveal } from "@/components/Reveal";
import { api, resolveImg } from "@/lib/api";

const TABS = [
  { key: "in-plant", label: "ModulArt In-Plant Office" },
  { key: "partition", label: "ModulArt Office Partition" },
];

/**
 * Layout 4c — Cinematic hero + tabs.
 *
 * Video content itself is deferred pending the user's decision on
 * storage method (YouTube embed vs MP4 upload vs both). Once decided,
 * the admin panel will manage the `videos` list per product and the
 * grid below will render them.
 *
 * URL query param `?tab=in-plant` or `?tab=partition` deep-links from
 * the "Videos" button on the Products page.
 */
export default function Videos() {
  const { search } = useLocation();
  const [products, setProducts] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/products");
        setProducts(data || []);
      } catch {
        /* silent */
      }
    })();
  }, []);

  const initialTab = useMemo(() => {
    const q = new URLSearchParams(search).get("tab");
    return TABS.find((t) => t.key === q) ? q : TABS[0].key;
  }, [search]);
  const [tab, setTab] = useState(initialTab);
  useEffect(() => setTab(initialTab), [initialTab]);

  const activeProduct = products.find((p) => p.category_key === tab);
  const videos = activeProduct?.videos || []; // populated once video management ships

  return (
    <div data-testid="videos-page">
      {/* CINEMATIC HERO */}
      <section className="relative min-h-[560px] flex items-center justify-start overflow-hidden bg-[#0e5f84]">
        <motion.img
          initial={{ scale: 1.05 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2, ease: [0.16, 1, 0.3, 1] }}
          src="https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0e5f84]/95 via-[#0e5f84]/70 to-transparent" />

        <div className="relative z-10 mx-auto max-w-[1400px] w-full px-6 lg:px-10 pt-40 pb-16 text-white">
          <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#78be27] mb-6">Watch & Learn</p>
          <h1 className="font-display font-bold text-white text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight max-w-3xl">
            <MaskLine delay={0.1}>Product films</MaskLine>
            <MaskLine delay={0.25}>in <span className="text-[#78be27]">action.</span></MaskLine>
          </h1>
          <Reveal delay={0.35}>
            <p className="mt-8 max-w-xl text-white/85 leading-relaxed text-lg">
              See our modular systems installed on the factory floor and inside corporate offices — from
              rapid on-site assembly to acoustic-insulated cabin walkthroughs.
            </p>
          </Reveal>
        </div>
      </section>

      {/* TABS */}
      <section className="border-b border-black/10 bg-white sticky top-[64px] z-30">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 flex gap-2 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`videos-tab-${t.key}`}
              className={`whitespace-nowrap px-6 py-5 font-mono-accent text-xs uppercase tracking-widest border-b-2 -mb-px transition-colors ${
                tab === t.key ? "border-[#3da8d8] text-[#3da8d8]" : "border-transparent text-[#52525B] hover:text-[#334155]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </section>

      {/* GRID */}
      <section className="mx-auto max-w-[1400px] px-6 lg:px-10 py-20 min-h-[40vh]">
        {videos.length === 0 ? (
          <div
            data-testid="videos-empty-state"
            className="border border-dashed border-black/15 bg-[#F9FAFB] px-8 py-16 flex flex-col items-center text-center"
          >
            <span className="w-16 h-16 flex items-center justify-center bg-[#3da8d8]/10 rounded-full mb-6">
              <Play size={28} className="text-[#3da8d8]" />
            </span>
            <p className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-[#3da8d8] mb-3">Coming soon</p>
            <h3 className="font-display text-2xl lg:text-3xl tracking-tight text-[#334155] mb-4 max-w-2xl">
              Video showcase for {TABS.find((t) => t.key === tab)?.label} is being prepared.
            </h3>
            <p className="text-[#52525B] max-w-xl leading-relaxed">
              In the meantime, download the catalogue below or reach out — we&apos;re happy to share raw
              installation footage from recent projects.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4 justify-center">
              {activeProduct?.pdf && (
                <a
                  href={resolveImg(activeProduct.pdf)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="videos-empty-catalogue"
                  className="btn-fill group inline-flex items-center gap-3 border border-[#3da8d8] px-7 py-3.5 font-mono-accent text-xs uppercase tracking-widest text-[#3da8d8] hover:text-white"
                >
                  Download catalogue <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </a>
              )}
              <Link
                to="/contact"
                data-testid="videos-empty-contact"
                className="group inline-flex items-center gap-3 border border-black/15 px-7 py-3.5 font-mono-accent text-xs uppercase tracking-widest text-[#334155] hover:border-[#78be27] hover:text-[#78be27] transition-colors"
              >
                Request footage <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {videos.map((v, i) => (
              <div key={i} data-testid={`video-card-${i}`} className="group">
                <div className="relative aspect-video bg-black overflow-hidden">
                  {v.thumbnail && (
                    <img src={resolveImg(v.thumbnail)} alt={v.title} className="w-full h-full object-cover opacity-90 group-hover:scale-105 transition-transform duration-[900ms]" />
                  )}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="w-16 h-16 rounded-full bg-white/95 flex items-center justify-center group-hover:bg-[#78be27] group-hover:text-white transition-colors">
                      <Play size={22} />
                    </span>
                  </div>
                </div>
                <h3 className="mt-4 font-display text-xl text-[#334155] leading-tight">{v.title}</h3>
                {v.subtitle && <p className="text-sm text-[#52525B] mt-1">{v.subtitle}</p>}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
